import { ExclamationCircleFilled, UserOutlined } from "@ant-design/icons";
import {
  FeatureFlag,
  SubscriptionPlanType,
  SubscriptionUpdateType,
  SubscriptionUser,
  UserSubscriptionInfo,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dropdown, Menu } from "antd";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { BillingActionResult } from "../../../actions/billing";
import {
  AddSeats,
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  ConfirmBox,
  UserList,
  Typography,
  FlexColumn,
  FlexRow,
  Button,
  EllipsisMenu,
  Tooltip,
  message,
} from "../../../components";
import { AVATAR_COLORS as avatarColors, COLORS } from "../../../constants";
import { BContext } from "../../../types";
import {
  capitalize,
  getActiveOtherUsersInDomain,
  getIsGmailLikeDomainFromEmail,
  getPricingSummary,
  planNameFromType,
  pluralize,
  sentryCapture,
  useApiContext,
  useTrack,
} from "../../../utils";
import { BillingContext } from "../Billing";
import { PriceSummaryProratedV2 } from "../components";
import { AddUserToPlanModal } from "../components/AddUserToPlanModal";
import { ContactSalesModal } from "../components/ContactSalesModal";
import { HelpContent } from "../components/HelpContent";
import { InviteUserToPlanModal } from "../components/InviteUserToPlanModal";

import { getPendingLicenseDrop } from "./members.utils";

const Container = styled.div`
  color: ${COLORS.black85};
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
`;

const TeamInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 60%;

  .ant-progress-inner {
    background: ${COLORS.black15};
  }
`;

const PricingSummaryWrap = styled.div`
  background: ${COLORS.coeblue11};
  padding: 8px 24px;
  border-radius: 8px;
  margin: auto;
  font-size: 14px;

  h3 {
    font-weight: bold;
    margin: 4px 0px 12px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1px;

  .ant-btn {
    border-radius: 0px !important;
    padding-left: 12px;
    padding-right: 12px;

    &:first-child {
      border-top-left-radius: 5px !important;
      border-bottom-left-radius: 5px !important;
    }

    &:last-child {
      border-top-right-radius: 5px !important;
      border-bottom-right-radius: 5px !important;
    }
  }
`;

const StyledCard = styled(FlexColumn)<{ background?: string }>`
  margin-bottom: 16px;
  border: none;
  padding: 16px;
  background: ${props => (props.background ? props.background : COLORS.white)};
  display: flex;
  border-radius: 6px;
`;

const AlertCard = ({ title, secondaryTitle }) => (
  <StyledCard background={COLORS.coeblue11} gap={0}>
    <Typography fontWeight={600}>{title}</Typography>
    <Typography color="textSecondary" fontSize="12px">
      Since {secondaryTitle}
    </Typography>
  </StyledCard>
);

const AlertMsg = styled(Alert)`
  margin-bottom: 8px;
  border: none;

  /* The designs bold the headline. */
  .ant-alert-message {
    font-weight: 600;
  }

  /* antd upsizes icons on described alerts. */
  &.ant-alert-with-description .ant-alert-icon {
    font-size: 14px;
    margin-top: 2px;
    margin-right: 8px;
  }
`;

/** Add/Remove licenses and their confirm step. */
const EditSeatsModal = styled(ConfirmBox)`
  && .ant-modal-content .ant-modal-body {
    /* 14px per the designs; ConfirmBox uses 16px. */
    font-size: 14px;

    /* Plus the footer's 5px, for 17px total. */
    padding-bottom: 12px;
  }

  && .ant-modal-content .ant-modal-body > .ant-alert {
    margin-bottom: 16px;

    /* A trailing banner uses the body padding. */
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

/** The pending-drop step, per the Figma. */
const PendingReductionModal = styled(ConfirmBox)`
  && .ant-modal-content {
    .ant-modal-body {
      /* Plus the footer's 5px, for 17px total. */
      padding-bottom: 12px;
    }

    .ant-modal-body > .ant-alert {
      /* 14px, unlike the 16px body copy. */
      font-size: 14px;

      /* 42px tall in the Figma; antd pads 8px. */
      padding-top: 12px;
      padding-bottom: 12px;
      margin-bottom: 16px;

      /* Bolder than AlertMsg's 600, per the Figma. */
      .ant-alert-message {
        font-weight: 700;
      }

      /* SVG: size is the only weight lever. */
      .ant-alert-icon {
        font-size: 16px;
      }
    }

    .ant-modal-footer {
      /* Balances the 24px sides, per the Figma. */
      padding-bottom: 24px;
    }
  }
`;

/** 149px in the Figma, ~132px at ~1.12x scale. */
const FOOTER_BUTTON_MIN_WIDTH = "132px";

const FOOTER_BUTTON_PROPS = { style: { minWidth: FOOTER_BUTTON_MIN_WIDTH } };

/** A filled secondary button, not a text link. */
const BackButton = styled(Button)`
  &&.ant-btn-default {
    min-width: ${FOOTER_BUTTON_MIN_WIDTH};
    background-color: ${COLORS.coeblue11};
    border-color: ${COLORS.coeblue11};

    &:active,
    &:hover,
    &:focus {
      background-color: ${COLORS.coeblue12};
      border-color: ${COLORS.coeblue12};
    }
  }
`;

const StyledUnderLine = styled.div`
  text-decoration: underline;
  text-decoration-style: dotted;
`;

export const ManageTeamV2 = () => {
  const track = useTrack();
  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  const {
    basePath,
    getUserSubscription,
    enableSubscriptionUpdate,
    userSubscription,
    updateSubscription,
    billingMetadata,
    additionalSeats,
    currentUserPlanManager,
    setAdditionalSeats,
    setContactSupportInfoBox,
    setPlanType,
    currentUserEmail,
    setBillingInterval,
    setDataSources,
    usersInCurrentPlan,

    otherUsersIndomain,

    planManagers,
    planMembers,
    isFreeUser,
    isManager,
    isAdmin,
    syncBilling,
    subscriptions,
    eventFrom,
  } = useContext(BillingContext) as BContext;

  const location = useLocation();
  useEffect(() => {
    // The link's `?from=` (e.g. "expansion_email_1" on the admin email's Manage Licenses link), so
    // analytics can tell which email drove the visit (ENG-8851), as billing_plan_chooser_viewed does.
    // A seatless admin gets there through /subscriptions, which drops the query string, so fall
    // back to the one Billing was opened with.
    track("billing_manage_team_viewed", {
      event_from: new URLSearchParams(location.search).get("from") ?? eventFrom,
    });
    document.title = "Manage Licenses - Coefficient Workspace";
  }, []);

  const history = useHistory();

  const subscriptionDetails = userSubscription?.subscription_details;
  const planType = subscriptionDetails?.plan_type || "";

  const adminEmails = planManagers.map(u => u.user?.email);

  const activeOtherUsersInDomain = getActiveOtherUsersInDomain(otherUsersIndomain).filter(
    u => adminEmails.indexOf(u.user?.email) === -1
  );

  const isGmailLikeDomain = getIsGmailLikeDomainFromEmail(currentUserEmail);

  const [showInviteUser, setShowInviteUser] = useState<boolean>(false);

  const [downgradeUserConfirm, setDowngradeUserConfirm] = useState<SubscriptionUser | null>(null);

  const [showEditSeatsPanel, setShowEditSeatsPanel] = useState<null | "add" | "remove">(null);

  const [showAddUserConfirm, setShowAddUserConfirm] = useState<boolean>(false);

  const [showAddUserToPlanModal, setShowAddUserToPlanModal] = useState<boolean>(false);

  // Plans with the AI Dashboards add-on are single-license: Add Licenses goes to sales.
  const [showAiDashboardsContactSales, setShowAiDashboardsContactSales] = useState<boolean>(false);

  const [initialUsersToAdd, setInitialUsersToAdd] = useState<Array<string>>([]);

  const [updateSeatsLoading, setUpdateSeatsLoading] = useState<boolean>(false);

  const [newlyInvitedUsers, setNewlyInvitedUsers] = useState<Array<string>>([]);

  const [addUserLoading, setAddUserLoading] = useState<boolean>(false);

  const [cancelingLicenseReduction, setCancelingLicenseReduction] = useState<boolean>(false);

  // Which flow the pending-drop step gates.
  const [pendingReductionNoticeFor, setPendingReductionNoticeFor] = useState<
    null | "add" | "remove"
  >(null);

  const newlyInvitedUsersEmailsSet = new Set(newlyInvitedUsers);

  const maxUsers = subscriptionDetails?.max_users ?? 0;

  // A license reduction is pending in Chargebee (takes effect at term end — see ENG-8298).
  // Shared with AddSeatsModal via getPendingLicenseDrop — see members.utils.ts for the rules.
  // `effectiveLicenses`/`hasScheduledDrop` are ungated (the pending change is real whichever
  // way the rollout flag falls, and the seat math has to account for it or a further edit
  // silently rewrites it); `showScheduledDowngradeNotice` carries the flag and gates every
  // place we *tell* the user about the drop.
  const {
    effectiveLicenses,
    dropsSeats: hasScheduledDrop,
    showsDropNotice: showScheduledDowngradeNotice,
  } = getPendingLicenseDrop(subscriptionDetails);

  /** True only if the drop was cleared. */
  const handleCancelLicenseReduction = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id || !enableSubscriptionUpdate) {
      return false;
    }
    setCancelingLicenseReduction(true);
    // Read before syncBilling refreshes it.
    const restored = maxUsers - effectiveLicenses;
    try {
      const res = await updateSubscription({
        update_type: SubscriptionUpdateType.RemoveScheduledChanges,
        subscription_id: subscriptionDetails.subscription_id,
      });
      if (res?.is_success) {
        track("billing_manage_team_scheduled_downgrade_cancelled", {
          current_plan_type: subscriptionDetails?.plan_type,
        });
        await syncBilling();
        message.success(`${restored} license${pluralize(restored)} restored`);
        return true;
      }
      message.error(res?.error_msg ?? "Something went wrong.");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setCancelingLicenseReduction(false);
    }
    return false;
  }, [
    subscriptionDetails,
    enableSubscriptionUpdate,
    updateSubscription,
    syncBilling,
    track,
    maxUsers,
    effectiveLicenses,
  ]);

  const maxSelfServeLicenses = subscriptionDetails?.max_self_serve_licenses;

  const isAtSelfServeCap = maxSelfServeLicenses != null && maxUsers >= maxSelfServeLicenses;

  const invitedUsers = userSubscription?.invite_users?.length ?? 0;

  const freeSeats = maxUsers - usersInCurrentPlan.length - invitedUsers;

  const currentPlan = billingMetadata?.plans.find(
    plan =>
      plan.plan_type === planType && plan.billing_interval === subscriptionDetails?.billing_interval
  );

  const ownerHasSeatOnPlan = Boolean(
    usersInCurrentPlan.find(user => user.user.email === currentUserPlanManager?.email)
  );

  const isPlanOwnerWithoutLicenseOnPlan = !ownerHasSeatOnPlan;

  const avatarColorOffset = isPlanOwnerWithoutLicenseOnPlan ? 1 : 0;

  const downgradeUser = userId => {
    const selectedUsers = usersInCurrentPlan
      .filter(u => u.user?.user_id !== userId)
      .map(u => u.user?.user_id);

    updateSubscriptionOnServer({
      plan: currentPlan,
      update_type: "reassignment",
      selected_user_ids: selectedUsers,
      invite_user_emails: (userSubscription?.invite_users ?? []).map(u => u.user?.email),
      selected_data_source_types: subscriptionDetails?.selected_data_source_types,
      coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
      subscription_id: userSubscription?.subscription_details?.subscription_id,
    });

    track("billing_manage_team_user_removed", {
      removed_user: usersInCurrentPlan.find(u => userId === u.user?.user_id)?.user?.email,
    });
  };

  const removeInvitedUser = email => {
    updateSubscriptionOnServer({
      plan: currentPlan,
      update_type: "reassignment",
      invite_user_emails: (userSubscription?.invite_users ?? [])
        .filter(u => u.user?.email !== email)
        .map(u => u.user?.email),
      selected_user_ids: usersInCurrentPlan.map(u => u.user?.user_id),
      selected_data_source_types: subscriptionDetails?.selected_data_source_types,
      coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
      subscription_id: userSubscription?.subscription_details?.subscription_id,
    });

    track("billing_manage_team_user_removed", {
      removed_user: userSubscription?.invite_users?.find(u => email === u.user?.email)?.user?.email,
    });
  };

  // ENG-8298 enforcement is on for every plan.
  const enforceScheduledLicenseReduction = true;

  // A pending drop blocks the add flow.
  const addedLicenseTotal = maxUsers + additionalSeats;

  // Mirrors the backend's license cap.
  const exceedsSelfServeCap =
    enforceScheduledLicenseReduction &&
    showEditSeatsPanel === "add" &&
    maxSelfServeLicenses != null &&
    addedLicenseTotal > maxSelfServeLicenses;

  const updateUserSeats = async () => {
    setUpdateSeatsLoading(true);
    const totalUserLicenses =
      showEditSeatsPanel === "add"
        ? addedLicenseTotal
        : showEditSeatsPanel === "remove"
        ? effectiveLicenses - additionalSeats
        : maxUsers;

    await updateSubscriptionOnServer({
      plan: currentPlan,
      update_type: "modify_subscription",
      total_user_licenses: totalUserLicenses,
      invite_user_emails: (userSubscription?.invite_users ?? []).map(u => u.user?.email),
      selected_data_source_types: subscriptionDetails?.selected_data_source_types,
      selected_user_ids: usersInCurrentPlan.map(u => u.user?.user_id),
      coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
      subscription_id: userSubscription?.subscription_details?.subscription_id,
    });
    setShowEditSeatsPanel(null);
    setShowAddUserConfirm(false);
    setUpdateSeatsLoading(false);
    track(
      showEditSeatsPanel === "add"
        ? "billing_manage_team_licenses_added"
        : "billing_manage_team_licenses_removed",
      {
        selected_num_users: totalUserLicenses,
        [showEditSeatsPanel === "add" ? "num_licenses_added" : "num_licenses_removed"]:
          additionalSeats,
      }
    );
  };

  const updateSubscriptionOnServer = async body => {
    message.info("Updating subscription...");
    const response = await updateSubscription(body);
    if (!response.is_success) {
      message.error(response.error_msg || "Uh-oh. Something went wrong. Please try again");
    } else {
      await syncBilling();
      message.success("Your subscription has been updated");
    }
  };

  const addPlanMangerMutation = useMutation(
    async (user: SubscriptionUser) => {
      message.info("Updating subscription...");
      const updatedInfo = await updateSubscription({
        update_type: SubscriptionUpdateType.AddPlanManager,
        subscription_id: userSubscription?.subscription_details?.subscription_id,
        new_plan_manager_user_id: user.user_id!,
      });

      if (updatedInfo.is_success) {
        await syncBilling();
      }

      return updatedInfo;
    },
    {
      onSuccess: (result: BillingActionResult) => {
        if (!result.is_success) {
          message.error(result.error_msg || "Uh-oh. Something went wrong. Please try again");
        } else {
          message.success("Your subscription has been updated");
        }
      },
      onError: (error: Error) => {
        sentryCapture({ error, name: "AddPlanManager" });
        message.error("Failed to update subscription");
      },
    }
  );

  const removePlanMangerMutation = useMutation(
    async (user: SubscriptionUser) => {
      message.info("Updating subscription...");
      const updatedInfo = await updateSubscription({
        update_type: SubscriptionUpdateType.RemovePlanManager,
        subscription_id: userSubscription?.subscription_details?.subscription_id,
        new_plan_manager_user_id: user.user_id!,
      });

      if (updatedInfo.is_success) {
        await syncBilling();
      }

      return updatedInfo;
    },
    {
      onSuccess: (result: BillingActionResult) => {
        if (!result.is_success) {
          message.error(result.error_msg || "Uh-oh. Something went wrong. Please try again");
        } else {
          message.success("Your subscription has been updated");
        }
      },
      onError: (error: Error) => {
        sentryCapture({ error, name: "RemovePlanManager" });
        message.error("Failed to update subscription");
      },
    }
  );

  // Premium "Request License" flow: seatless users asking to be added to this plan, surfaced to
  // managers as a snack bar (count + "Add users to plan"). Adding a user to the plan auto-resolves
  // their request server-side, so we just refetch the count when the add-users modal closes.
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();
  const licenseRequestsSubscriptionId = subscriptionDetails?.subscription_id;
  const licenseRequestsQueryKey = ["LicenseRequests", licenseRequestsSubscriptionId];

  const { data: licenseRequests = [] } = useQuery({
    queryKey: licenseRequestsQueryKey,
    // Scope the request to the subscription being viewed so a multi-subscription domain only
    // surfaces (and lets us assign) this plan's requesters. `enabled` guarantees the id is set.
    queryFn: async () =>
      (await apiClient.listLicenseRequests({ subscription_id: licenseRequestsSubscriptionId! }))
        .data.requests,
    // ENG-8853: admins (not just plan managers) can see/act on requests.
    enabled: (isManager || isAdmin) && !!licenseRequestsSubscriptionId,
  });

  useEffect(() => {
    // set the selected billing details to populate the pricing summary
    if (subscriptionDetails?.plan_type) {
      setPlanType(subscriptionDetails?.plan_type);
    }
    if (subscriptionDetails?.billing_interval) {
      setBillingInterval(subscriptionDetails?.billing_interval);
    }
    if (subscriptionDetails?.selected_data_source_types) {
      setDataSources(subscriptionDetails?.selected_data_source_types);
    }
  }, [usersInCurrentPlan]);

  const openAddUserSeatsPopUp = () => {
    // Plans with the AI Dashboards add-on are single-license only (the server also
    // hard-blocks this) — buying more seats goes through sales.
    if (enableSelfServeAiDashboards && subscriptionDetails?.has_web_dashboard_addon) {
      setShowAiDashboardsContactSales(true);
      return;
    }
    // Skip at the cap: clearing the drop won't help.
    if (enforceScheduledLicenseReduction && showScheduledDowngradeNotice && !isAtSelfServeCap) {
      setPendingReductionNoticeFor("add");
      return;
    }
    // Redesigned path explains the cap in-dialog; un-enforced plans keep the legacy box.
    if (!enforceScheduledLicenseReduction && isAtSelfServeCap) {
      setContactSupportInfoBox({
        title: "Manage Licenses",
        message:
          "Pro plans support up to 5 user licenses. Please contact sales@coefficient.io for volume pricing.",
      });
      return;
    }
    setAdditionalSeats(1);
    setShowEditSeatsPanel("add");
  };

  const openRemoveUserSeatsPopUp = () => {
    // Removals size off the restored count.
    if (showScheduledDowngradeNotice) {
      setPendingReductionNoticeFor("remove");
      return;
    }
    if (subscriptionDetails?.max_users) {
      setAdditionalSeats(1);
    }
    setShowEditSeatsPanel("remove");
  };

  const subscribedAdditionalUserSeats =
    (subscriptionDetails?.max_users ?? 0) -
    (userSubscription?.invite_users ?? []).length -
    usersInCurrentPlan.length;

  // Licenses that can still be *removed*, which is a different question: removal lands at
  // renewal, so the ceiling is what the plan will have then (effectiveLicenses), not today's
  // count — otherwise a second removal rewrites the pending change instead of stacking onto it.
  // Clamped because assigned users may already exceed a pending reduction; the backend's
  // `num_user_licenses >= assigned + invited` assert would turn a negative into a 500.
  const removableUserSeats = Math.max(
    effectiveLicenses - (userSubscription?.invite_users ?? []).length - usersInCurrentPlan.length,
    0
  );

  // for computing new pricing summary
  const planMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === subscriptionDetails?.plan_type && p.billing_interval === "monthly"
    )?.per_user_plan?.per_item_monthly_price_usd || 0;

  const planAnnualMonthlyPrice: number =
    billingMetadata?.plans.find(
      p => p.plan_type === subscriptionDetails?.plan_type && p.billing_interval === "annually"
    )?.per_user_plan?.per_item_monthly_price_usd || 0;

  // Mirrors totalUserLicenses so the confirm-step price summary prices the count that will
  // actually be in effect after the change.
  const numberOfUserInPlan: number =
    showEditSeatsPanel === "add"
      ? addedLicenseTotal
      : showEditSeatsPanel === "remove"
      ? effectiveLicenses - additionalSeats
      : maxUsers;

  const newPriceSummary = getPricingSummary(
    subscriptionDetails?.plan_type,
    subscriptionDetails?.billing_interval ?? "annually", // billing interval
    planMonthlyPrice, // monthly price for the plan (monthly interval)
    planAnnualMonthlyPrice, // monthly price for the plan (annual interval)
    currentPlan?.per_additional_data_source_addon?.per_item_monthly_price_usd || 0, // addtional data source cost
    numberOfUserInPlan, // # of users
    0, // # of data source should be 0 for add/remove seats to not display item
    0, // AI Dashboards addon price (monthly interval)
    0, // AI Dashboards addon price (annual interval)
    false, // include AI Dashboards addon
    billingMetadata?.coupon_discount_percentage
  );

  const manageLicensesMenu = (
    <Menu>
      <Menu.Item key={"add-licenses"}>
        {/* With the AI Dashboards add-on this opens Contact Sales (openAddUserSeatsPopUp). */}
        <a href="javascript:void(0)" onClick={openAddUserSeatsPopUp}>
          Add Licenses
        </a>
      </Menu.Item>
      <Menu.Item key={"remove-licenses"}>
        <a href="javascript:void(0)" onClick={openRemoveUserSeatsPopUp}>
          Remove Licenses
        </a>
      </Menu.Item>
    </Menu>
  );

  const renderUserMenu = (user: SubscriptionUser) => {
    if (!user.user_id) {
      // invited user
      return (
        <Menu>
          <Menu.Item key="change-to-manager" disabled>
            <Tooltip title="Invite user cannot be made manager">Change to plan manager</Tooltip>
          </Menu.Item>
          <Menu.Item key={"remove-invited-user"} onClick={() => removeInvitedUser(user.email)}>
            Remove user from plan
          </Menu.Item>
        </Menu>
      );
    }

    return (
      <Menu>
        <Menu.Item
          key="change-to-manager"
          onClick={async () => await addPlanMangerMutation.mutateAsync(user)}
        >
          Change to plan manager
        </Menu.Item>

        <Menu.Item key={"remove-user"} onClick={() => setDowngradeUserConfirm(user)}>
          Remove user from plan
        </Menu.Item>
      </Menu>
    );
  };

  const renderOtherUserInfo = (user: UserSubscriptionInfo) => {
    const managedByUser = user.managed_by_user;
    const onAnotherPlan = !!managedByUser;

    if (readOnly && !onAnotherPlan) {
      return null;
    }

    if (readOnly && onAnotherPlan) {
      return (
        <>
          <Typography color="textSecondary" fontWeight={500}>
            <Tooltip
              title={`Plan managed by ${managedByUser?.first_name || " "} ${
                managedByUser?.last_name || " "
              }`}
            >
              <StyledUnderLine>on another plan</StyledUnderLine>
            </Tooltip>
          </Typography>
          <Button type="text" size="small"></Button>
        </>
      );
    }

    if (onAnotherPlan) {
      return (
        <>
          <Typography color="textSecondary" fontWeight={500}>
            <Tooltip
              title={`Plan managed by ${managedByUser?.first_name || " "} ${
                managedByUser?.last_name || " "
              }`}
            >
              <StyledUnderLine>on another plan</StyledUnderLine>
            </Tooltip>
          </Typography>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="change-to-manager"
                  onClick={async () => await addPlanMangerMutation.mutateAsync(user.user)}
                >
                  Change to plan manager
                </Menu.Item>
              </Menu>
            }
            placement="bottomLeft"
          >
            <Button type="text" size="small">
              <EllipsisMenu />
            </Button>
          </Dropdown>
        </>
      );
    }

    return (
      <>
        <Button
          noPadding
          type="link"
          disabled={addUserLoading}
          onClick={() => {
            const userId = user.user?.user_id;
            handleAddUserToPlan(userId, user.user.email);
          }}
        >
          Add user to plan
        </Button>
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="change-to-manager"
                onClick={async () => await addPlanMangerMutation.mutateAsync(user.user)}
              >
                Change to plan manager
              </Menu.Item>

              <Menu.Item
                key={"add-user-to-plan"}
                disabled={addUserLoading}
                onClick={() => handleAddUserToPlan(user.user?.user_id, user.user?.email)}
              >
                Add user to plan
              </Menu.Item>
            </Menu>
          }
          placement="bottomLeft"
        >
          <Button type="text" size="small">
            <EllipsisMenu />
          </Button>
        </Dropdown>
      </>
    );
  };

  const renderAdminInfo = (user: UserSubscriptionInfo) => {
    const managedByUser = user.managed_by_user;
    const onAnotherPlan = managedByUser?.email !== subscriptionDetails?.subscription_owner_email;

    if (!managedByUser) {
      return (
        <Button
          noPadding
          type="link"
          disabled={addUserLoading}
          onClick={() => {
            const userId = user.user?.user_id;
            handleAddUserToPlan(userId, user.user.email);
          }}
        >
          Add user to plan
        </Button>
      );
    }

    if (onAnotherPlan) {
      return (
        <Typography color="textSecondary" fontWeight={500}>
          <Tooltip
            title={`Plan managed by ${managedByUser.first_name || " "} ${
              managedByUser.last_name || " "
            }`}
          >
            <StyledUnderLine> on another plan</StyledUnderLine>
          </Tooltip>
        </Typography>
      );
    }

    return (
      <Typography color="textSecondary" fontWeight={500}>
        on this plan
      </Typography>
    );
  };

  const renderAdminMenu = (info: UserSubscriptionInfo) => {
    const user = info.user;
    const managedByUser = info.managed_by_user;
    const isPlanOwner = info.user.email === subscriptionDetails?.subscription_owner_email;
    const onAnotherPlan =
      managedByUser && managedByUser?.email !== subscriptionDetails?.subscription_owner_email;

    if (isPlanOwner && !managedByUser) {
      return (
        <Menu>
          <Menu.Item key="change-to-member" disabled>
            <Tooltip title="Plan creator cannot be removed as plan manager">
              Remove as plan manager
            </Tooltip>
          </Menu.Item>
          <Menu.Item
            key={"add-user-to-plan"}
            disabled={addUserLoading}
            onClick={() => handleAddUserToPlan(user?.user_id, user?.email)}
          >
            Add user to plan
          </Menu.Item>
        </Menu>
      );
    }

    if (isPlanOwner && onAnotherPlan) {
      return (
        <Menu>
          <Menu.Item key="change-to-member" disabled>
            <Tooltip title="Plan creator cannot be removed as plan manager">
              Remove as plan manager
            </Tooltip>
          </Menu.Item>
        </Menu>
      );
    }

    if (isPlanOwner && managedByUser) {
      return (
        <Menu>
          <Menu.Item key="change-to-member" disabled>
            <Tooltip title="Plan creator cannot be removed as plan manager">
              Remove as plan manager
            </Tooltip>
          </Menu.Item>
          <Menu.Item key={"remove-user"} onClick={() => setDowngradeUserConfirm(user)}>
            Remove user from plan
          </Menu.Item>
        </Menu>
      );
    }

    if (onAnotherPlan) {
      return (
        <Menu>
          <Menu.Item
            key="change-to-member"
            onClick={async () => await removePlanMangerMutation.mutateAsync(user)}
          >
            Remove as plan manager
          </Menu.Item>
        </Menu>
      );
    }

    if (!managedByUser) {
      return (
        <Menu>
          <Menu.Item
            key="change-to-member"
            onClick={async () => await removePlanMangerMutation.mutateAsync(user)}
          >
            Remove as plan manager
          </Menu.Item>
          <Menu.Item
            key={"add-user-to-plan"}
            disabled={addUserLoading}
            onClick={() => handleAddUserToPlan(user?.user_id, user?.email)}
          >
            Add user to plan
          </Menu.Item>
        </Menu>
      );
    }

    return (
      <Menu>
        <Menu.Item
          key="change-to-member"
          onClick={async () => await removePlanMangerMutation.mutateAsync(user)}
        >
          Remove as plan manager
        </Menu.Item>
        <Menu.Item key={"remove-user"} onClick={() => setDowngradeUserConfirm(user)}>
          Remove user from plan
        </Menu.Item>
      </Menu>
    );
  };

  const handleAddUsers = () => {
    setShowAddUserToPlanModal(true);
  };

  // From the license-request snack bar: open the add-users modal pre-filled with the requesters'
  // emails, so the manager just confirms rather than retyping them.
  const handleAddRequestedUsers = () => {
    setInitialUsersToAdd(licenseRequests.map(request => request.user_email));
    setShowAddUserToPlanModal(true);
  };

  const handleAddUserToPlan = async (userId: string | undefined, email: string | undefined) => {
    if (!userId || addUserLoading) {
      return;
    }

    if (freeSeats > 0) {
      setAddUserLoading(true);
      try {
        await upgradeUser(userId);
      } finally {
        setAddUserLoading(false);
      }
    } else {
      if (!email) {
        return;
      }
      setInitialUsersToAdd([email]);
      setShowAddUserToPlanModal(true);
    }
  };

  const upgradeUser = async (userId: string) => {
    const selectedUsers = usersInCurrentPlan.map(u => u.user?.user_id);

    selectedUsers.push(userId);

    await updateSubscriptionOnServer({
      plan: currentPlan,
      update_type: "reassignment",
      invite_user_emails: (userSubscription?.invite_users ?? []).map(u => u.user?.email),
      selected_user_ids: selectedUsers,
      selected_data_source_types: subscriptionDetails?.selected_data_source_types,
      coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
      subscription_id: userSubscription?.subscription_details?.subscription_id,
    });

    track("billing_manage_team_user_added", {
      event_from: "user_list",
      added_users: otherUsersIndomain
        .filter(u => userId === u.user?.user_id)
        .map(u => u.user?.email),
    });
  };

  // ENG-8853: a workspace admin (not just a plan manager) can see and act on the page -- notably
  // the pending license-request snack bar -- so they aren't locked out of granting a seat.
  const readOnly = isGmailLikeDomain || (!isManager && !isAdmin);

  const nextRenewalDtStr = moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM D, YYYY");

  const removeWithNothingToRemove = showEditSeatsPanel === "remove" && removableUserSeats === 0;

  const scheduledDropHeadline = `You have ${maxUsers} license${pluralize(
    maxUsers
  )} now, reducing to ${effectiveLicenses} on ${nextRenewalDtStr}`;

  return (
    <Container>
      <Breadcrumb>
        {!!subscriptions?.other_subscriptions?.length && (
          <BreadcrumbItem>
            <Link to={{ pathname: `${basePath}/subscriptions`, search: window.location.search }}>
              Subscriptions
            </Link>
          </BreadcrumbItem>
        )}
        <BreadcrumbItem>
          <Link to={{ pathname: `${basePath}/subscription`, search: window.location.search }}>
            Plans & Billing
          </Link>
        </BreadcrumbItem>
        <BreadcrumbItem>Manage Licenses</BreadcrumbItem>
      </Breadcrumb>

      <Content>
        <TeamInfoContainer>
          <FlexRow style={{ justifyContent: "space-between", marginBottom: "24px" }}>
            <Typography fontSize="26px" fontWeight={700}>
              Manage Licenses
            </Typography>
            <FlexRow gap={0}>
              {!readOnly && (
                <ButtonGroup>
                  <Button type="primary" onClick={handleAddUsers}>
                    Add Users
                  </Button>
                  {["enterprise", "custom", "pro"].includes(planType) && (
                    <Dropdown
                      overlay={enableSubscriptionUpdate ? manageLicensesMenu : <></>}
                      placement="bottomLeft"
                    >
                      <Button
                        type="primary"
                        style={{ marginRight: "8px" }}
                        onClick={() => {
                          if (!enableSubscriptionUpdate) {
                            setContactSupportInfoBox({ title: "Manage Licenses" });
                          }
                        }}
                      >
                        <EllipsisMenu height={"12px"} fill="currentColor" />
                      </Button>
                    </Dropdown>
                  )}
                </ButtonGroup>
              )}
            </FlexRow>
          </FlexRow>

          {showScheduledDowngradeNotice && (
            <AlertMsg
              type="warning"
              description={
                <FlexRow justifyContent="space-between" gap={2}>
                  <Typography fontWeight={600}>
                    You have {maxUsers} license{pluralize(maxUsers)} now, reducing to{" "}
                    {effectiveLicenses} on{" "}
                    {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM D, YYYY")}
                  </Typography>
                  {!readOnly && enableSubscriptionUpdate && (
                    <>
                      {" "}
                      <Button
                        type="link"
                        noPadding
                        loading={cancelingLicenseReduction}
                        onClick={handleCancelLicenseReduction}
                        style={{
                          fontWeight: 600,
                          padding: 0,
                          height: "auto",
                          display: "inline-flex",
                          alignItems: "flex-end",
                        }}
                      >
                        Undo
                      </Button>
                    </>
                  )}
                </FlexRow>
              }
            />
          )}

          {isFreeUser && (
            <AlertCard
              title={`You’re on the ${capitalize(planType)} plan`}
              secondaryTitle={moment(subscriptionDetails?.subscription_active_since_dt).format(
                "MMM DD, YYYY"
              )}
            />
          )}

          {!readOnly && licenseRequests.length > 0 && (
            <AlertMsg
              type="info"
              showIcon
              closable
              message={
                <FlexRow style={{ justifyContent: "space-between", alignItems: "center" }}>
                  <span>
                    {licenseRequests.length} license request{pluralize(licenseRequests.length)}
                  </span>
                  <Button noPadding type="link" onClick={handleAddRequestedUsers}>
                    Add users to plan
                  </Button>
                </FlexRow>
              }
            />
          )}

          <FlexColumn>
            <Typography fontWeight={"bold"} fontSize="18px" style={{ marginBottom: "8px" }}>
              Plan managers
            </Typography>

            {planManagers.map((user, i) => (
              <UserList
                key={i}
                avatarColor={avatarColors[avatarColorOffset + (i % avatarColors.length)]}
                name={`${user.user?.first_name || user.user.email} ${user.user?.last_name || ""}`}
                email={user.user?.email || ""}
                userId={user.user?.user_id || ""}
                plan={user.plan_type ?? SubscriptionPlanType.FreeTrial}
                footer={
                  !readOnly && (
                    <FlexRow gap={2} style={{ justifyContent: "flex-end" }}>
                      <FlexRow gap={2} style={{ justifyContent: "space-between" }}>
                        {renderAdminInfo(user)}
                        <Dropdown overlay={renderAdminMenu(user)} placement="bottomLeft">
                          <Button type="text" size="small">
                            <EllipsisMenu />
                          </Button>
                        </Dropdown>
                      </FlexRow>
                    </FlexRow>
                  )
                }
              />
            ))}
          </FlexColumn>

          <FlexColumn style={{ marginTop: "24px" }}>
            <Typography fontWeight={"bold"} fontSize="18px" style={{ marginBottom: "8px" }}>
              Plan members
            </Typography>

            {planMembers.length === 0 && subscribedAdditionalUserSeats === 0 && (
              <UserList
                className="assignUsers"
                avatarColor={COLORS.black25}
                avatarIcon={<UserOutlined />}
                name={`No member on this plan`}
                userId={""}
                isSelectable={false}
                footer={
                  !readOnly && (
                    <FlexRow gap={2} style={{ justifyContent: "flex-end" }}>
                      <Button noPadding type="link" onClick={handleAddUsers}>
                        Add user to plan
                      </Button>
                      <Button></Button>
                    </FlexRow>
                  )
                }
              />
            )}

            {planMembers.map((user, i) => (
              <UserList
                key={i}
                avatarColor={avatarColors[avatarColorOffset + (i % avatarColors.length)]}
                name={`${user.user?.first_name || user.user.email} ${user.user?.last_name || ""}`}
                email={user.user?.email || ""}
                userId={user.user?.user_id || ""}
                plan={user.plan_type ?? SubscriptionPlanType.FreeTrial}
                footer={
                  <FlexRow gap={2} style={{ justifyContent: "flex-end" }}>
                    <FlexRow gap={2} style={{ justifyContent: "space-between" }}>
                      <Typography color="textSecondary" fontWeight={500}>
                        on this plan
                      </Typography>
                      {readOnly ? (
                        <Button type="text" size="small" />
                      ) : (
                        <Dropdown overlay={renderUserMenu(user.user)} placement="bottomLeft">
                          <Button type="text" size="small">
                            <EllipsisMenu />
                          </Button>
                        </Dropdown>
                      )}
                    </FlexRow>
                  </FlexRow>
                }
              />
            ))}

            {subscribedAdditionalUserSeats > 0 && !readOnly && (
              <UserList
                className="assignUsers"
                avatarColor={COLORS.black25}
                avatarIcon={<UserOutlined />}
                name={`${subscribedAdditionalUserSeats} Unassigned License${pluralize(
                  subscribedAdditionalUserSeats
                )}`}
                userId={""}
                isSelectable={false}
                footer={
                  !readOnly && (
                    <FlexRow gap={2} style={{ justifyContent: "flex-end" }}>
                      <Button noPadding type="link" onClick={handleAddUsers}>
                        Assign license
                      </Button>
                      <Button></Button>
                    </FlexRow>
                  )
                }
              />
            )}
          </FlexColumn>

          {activeOtherUsersInDomain.length > 0 && (
            <FlexColumn style={{ marginTop: "24px" }}>
              <Typography fontWeight={700} variant="h6">
                Other users from your domain
              </Typography>
              {activeOtherUsersInDomain.map((user, i) => (
                <UserList
                  key={i}
                  avatarColor={
                    avatarColors[
                      (planManagers.length + i + avatarColorOffset) % avatarColors.length
                    ]
                  }
                  name={`${user.user?.first_name || user.user.email} ${user.user?.last_name || ""}`}
                  email={user.user?.email || ""}
                  userId={user.user?.user_id || ""}
                  plan={user.plan_type ?? SubscriptionPlanType.FreeTrial}
                  footer={
                    <FlexRow style={{ justifyContent: "flex-end" }}>
                      {renderOtherUserInfo(user)}
                    </FlexRow>
                  }
                />
              ))}
            </FlexColumn>
          )}
        </TeamInfoContainer>

        <HelpContent />
      </Content>

      {/* Confirm Downgrade to free popup */}
      <ConfirmBox
        closable={true}
        title={`Remove user from ${planNameFromType(planType)}?`}
        visible={downgradeUserConfirm !== null}
        okText={`Remove & downgrade`}
        cancelText="Cancel"
        cancelButton={
          <Button type="link" noPadding>
            Cancel
          </Button>
        }
        onOk={() => {
          if (downgradeUserConfirm?.user_id) {
            downgradeUser(downgradeUserConfirm?.user_id);
          } else if (downgradeUserConfirm?.email) {
            removeInvitedUser(downgradeUserConfirm?.email);
          }
          setDowngradeUserConfirm(null);
        }}
        onCancel={() => setDowngradeUserConfirm(null)}
        width={600}
      >
        <>
          <p>
            {downgradeUserConfirm?.first_name ?? downgradeUserConfirm?.email}{" "}
            {downgradeUserConfirm?.last_name} will be downgraded to the Free plan.
          </p>
          <div
            style={{
              borderRadius: "8px",
              color: COLORS.black85,
              background: COLORS.yellowBg2,
              padding: "16px",
              margin: "12px 0px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              <ExclamationCircleFilled style={{ color: COLORS.orange2 }} /> They&apos;ll lose access
              to:
            </div>
            <ul style={{ margin: 0, paddingLeft: "40px" }}>
              <li style={{ margin: "8px 0px" }}>Scheduled imports (manual refresh only)</li>
              <li style={{ margin: "8px 0px" }}>
                Other users won&apos;t be able to refresh{" "}
                {downgradeUserConfirm?.first_name ?? downgradeUserConfirm?.email}
                &apos;s imports
              </li>
              <li style={{ margin: "8px 0px" }}>Exporting to data sources</li>
              {enableSelfServeAiDashboards && subscriptionDetails?.has_web_dashboard_addon && (
                <li style={{ margin: "8px 0px" }}>
                  AI Dashboards will stop refreshing and be archived
                </li>
              )}
            </ul>
          </div>
        </>
      </ConfirmBox>

      {/* Proceed clears the drop first. */}
      <PendingReductionModal
        title="Pending license reduction"
        visible={pendingReductionNoticeFor !== null}
        width={560}
        okText="Proceed"
        showCancel={false}
        confirmLoading={cancelingLicenseReduction}
        onOk={async () => {
          const nextPanel = pendingReductionNoticeFor;
          if (await handleCancelLicenseReduction()) {
            setPendingReductionNoticeFor(null);
            setAdditionalSeats(1);
            setShowEditSeatsPanel(nextPanel);
          }
        }}
        onCancel={() => setPendingReductionNoticeFor(null)}
        closable
        maskClosable={true}
      >
        <AlertMsg type="warning" showIcon message={scheduledDropHeadline} />
        {/* Last in the body: no trailing margin. */}
        <p style={{ marginBottom: 0 }}>
          To{" "}
          {pendingReductionNoticeFor === "remove"
            ? "remove user licenses from"
            : "add user licenses to"}{" "}
          your plan, you need to cancel your pending reduction. Would you like to proceed?
        </p>
      </PendingReductionModal>

      {/* Add/Remove licenses. Every redesigned element is keyed off the enforcement gate. */}
      <EditSeatsModal
        title={
          showEditSeatsPanel === "add"
            ? enforceScheduledLicenseReduction
              ? "Add user licenses"
              : "Add additional user licenses"
            : "Remove user licenses"
        }
        visible={showEditSeatsPanel !== null && !showAddUserConfirm}
        okText={removeWithNothingToRemove ? "Got it" : "Next"}
        confirmDisabled={exceedsSelfServeCap}
        confirmLoading={updateSeatsLoading}
        onOk={() => {
          if (removeWithNothingToRemove) {
            setShowEditSeatsPanel(null);
            return;
          }
          if (additionalSeats <= 0) {
            return;
          }
          setShowAddUserConfirm(true);
        }}
        width={560}
        okButtonProps={FOOTER_BUTTON_PROPS}
        closable={enforceScheduledLicenseReduction}
        onCancel={() => {
          setShowEditSeatsPanel(null);
        }}
        maskClosable={true}
        cancelButton={
          enforceScheduledLicenseReduction && !removeWithNothingToRemove ? (
            // Sits in the footer's cancel slot, whose wrapper closes the dialog on click.
            <Typography
              color="textSecondary"
              fontSize="12px"
              onClick={e => e.stopPropagation()}
              style={{ cursor: "default" }}
            >
              {showEditSeatsPanel === "add"
                ? "You can assign licenses to users once you purchase."
                : "Only unassigned licenses can be removed."}
            </Typography>
          ) : undefined
        }
      >
        <>
          {/* Over the cap nothing can go through. */}
          {exceedsSelfServeCap && (
            <AlertMsg
              type="warning"
              showIcon
              icon={<ExclamationCircleFilled />}
              message={`${planNameFromType(
                planType
              )} plans include up to ${maxSelfServeLicenses} licenses`}
              description={
                <>
                  Contact <a href="mailto:sales@coefficient.io">sales@coefficient.io</a> to upgrade.
                </>
              }
            />
          )}
          {showEditSeatsPanel === "add" &&
            (enforceScheduledLicenseReduction ? (
              !exceedsSelfServeCap && (
                <p>
                  You currently have {maxUsers} user license{pluralize(maxUsers)}
                </p>
              )
            ) : (
              <p>You can assign licenses to users once you purchase.</p>
            ))}
          {showEditSeatsPanel === "remove" && removableUserSeats > 0 && (
            <p>
              {enforceScheduledLicenseReduction ? (
                <>
                  You currently have {removableUserSeats} unassigned license
                  {pluralize(removableUserSeats)} that you can remove from your plan.
                </>
              ) : (
                <>
                  You have {removableUserSeats} unassigned license
                  {pluralize(removableUserSeats)} on your plan that you can remove.
                </>
              )}
            </p>
          )}
          {removeWithNothingToRemove && (
            <p>
              {/* Ungated: removableUserSeats is ungated too, so with the flag off and a drop
                  pending this must still explain why unassigning won't free anything. */}
              {hasScheduledDrop ? (
                <>
                  You can only remove unassigned licenses from your plan. All licenses are currently
                  assigned or already scheduled to be removed.
                </>
              ) : (
                <>
                  You can only remove unassigned licenses from your plan. All licenses are currently
                  assigned. Please unassign a license first.
                </>
              )}
            </p>
          )}
          {!removeWithNothingToRemove && (
            <AddSeats
              onChange={val => {
                if (val < 1) {
                  setAdditionalSeats(1);
                  return;
                }
                if (showEditSeatsPanel === "remove") {
                  setAdditionalSeats(Math.min(val, removableUserSeats));
                  return;
                }
                // Not clamped to the cap: going over is what surfaces the banner.
                if (enforceScheduledLicenseReduction) {
                  setAdditionalSeats(val);
                  return;
                }
                const capped =
                  maxSelfServeLicenses != null
                    ? Math.min(val, maxSelfServeLicenses - maxUsers)
                    : val;
                setAdditionalSeats(Math.max(capped, 1));
              }}
              onAddUser={() => {
                if (showEditSeatsPanel === "remove") {
                  if (additionalSeats < removableUserSeats) {
                    setAdditionalSeats(additionalSeats + 1);
                  }
                  return;
                }
                const maxAdditional =
                  !enforceScheduledLicenseReduction && maxSelfServeLicenses != null
                    ? maxSelfServeLicenses - maxUsers
                    : Infinity;
                if (additionalSeats < maxAdditional) {
                  setAdditionalSeats(additionalSeats + 1);
                }
              }}
              onRemoveUser={() => {
                if (additionalSeats > 1) {
                  setAdditionalSeats(additionalSeats - 1);
                }
              }}
              userCount={additionalSeats}
            />
          )}
          {/* Legacy only: the redesign uses padding. */}
          {!enforceScheduledLicenseReduction && <div style={{ height: "50px" }}></div>}
        </>
      </EditSeatsModal>

      {/* Confirm Add/Remove user seats popup */}
      <EditSeatsModal
        title={`Confirm plan changes`}
        visible={showAddUserConfirm}
        okText={
          showEditSeatsPanel === "add"
            ? enforceScheduledLicenseReduction
              ? `Confirm & Pay`
              : `Confirm & Pay Now`
            : `Confirm changes`
        }
        cancelText={`Cancel`}
        onOk={() => updateUserSeats()}
        closable={enforceScheduledLicenseReduction}
        // X closes the flow; Back returns to the stepper (its handler stops propagation).
        onCancel={() => {
          setShowAddUserConfirm(false);
          setShowEditSeatsPanel(null);
        }}
        cancelButton={
          enforceScheduledLicenseReduction ? (
            <BackButton
              type="default"
              onClick={e => {
                e.stopPropagation();
                setShowAddUserConfirm(false);
              }}
            >
              Back
            </BackButton>
          ) : undefined
        }
        width={560}
        okButtonProps={FOOTER_BUTTON_PROPS}
        confirmLoading={updateSeatsLoading}
      >
        <>
          <p>
            {!enforceScheduledLicenseReduction ? (
              <>
                {additionalSeats} Pro user license{pluralize(additionalSeats)} will be{" "}
                {showEditSeatsPanel === "add" ? "added to" : "removed from"} your plan
                {showEditSeatsPanel === "remove"
                  ? ` on your next renewal date (${nextRenewalDtStr})`
                  : ""}
                .
              </>
            ) : showEditSeatsPanel === "add" ? (
              <>
                {additionalSeats} additional {planNameFromType(planType)} user license
                {pluralize(additionalSeats)} will be added to your plan.
              </>
            ) : (
              <>
                {additionalSeats} {planNameFromType(planType)} user license
                {pluralize(additionalSeats)} will be removed from your plan on your next renewal
                date ({nextRenewalDtStr}).
              </>
            )}
          </p>
          <PricingSummaryWrap>
            <PriceSummaryProratedV2
              editType={showEditSeatsPanel === "add" ? "add-seats" : "remove-seats"}
              newPriceSummary={newPriceSummary}
              seats={additionalSeats}
              hideTotalPerMonth={enforceScheduledLicenseReduction}
            />
          </PricingSummaryWrap>
        </>
      </EditSeatsModal>

      <ContactSalesModal
        showModal={showAiDashboardsContactSales}
        description={`To add additional users to your ${planNameFromType(
          planType
        )} plan, with AI Dashboards, contact sales.`}
        hideTextarea
        onCancel={() => setShowAiDashboardsContactSales(false)}
      />

      {/* Invite users popup */}
      <InviteUserToPlanModal open={showInviteUser} onClose={() => setShowInviteUser(false)} />

      {/* Add Users popup */}
      {showAddUserToPlanModal && (
        <AddUserToPlanModal
          onInvitedCb={invitedUsers => {
            setNewlyInvitedUsers(invitedUsers);
          }}
          open={showAddUserToPlanModal}
          initialUsersToAdd={initialUsersToAdd}
          onClose={() => {
            setShowAddUserToPlanModal(false);
            setInitialUsersToAdd([]);
            // Users added here auto-resolve their license requests server-side; refetch so the
            // snack-bar count reflects it.
            queryClient.invalidateQueries(licenseRequestsQueryKey);
          }}
        />
      )}
    </Container>
  );
};
